import { CalendarClock, ListChecks, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import type { TemplateItem } from '../../../api/templates';

/** The template kinds a new event can start from, in the order shown. */
export const STARTER_TEMPLATE_TYPES = ['checklist', 'timeline', 'kibuddim'] as const;
export type StarterTemplateType = (typeof STARTER_TEMPLATE_TYPES)[number];

const GROUPS: Record<StarterTemplateType, { label: string; icon: ReactNode }> = {
  checklist: { label: 'Task lists', icon: <ListChecks size={15} /> },
  timeline: { label: 'Timelines', icon: <CalendarClock size={15} /> },
  kibuddim: { label: 'Kibuddim', icon: <Users size={15} /> },
};

export function isStarterTemplate(
  t: TemplateItem,
): t is TemplateItem & { _type: StarterTemplateType } {
  return (STARTER_TEMPLATE_TYPES as readonly string[]).includes(t._type) && !t.isArchived;
}

/** The templates written for this event type — ticked for a new event by default. */
export function suggestedTemplateIds(templates: TemplateItem[], eventTypeId: string): string[] {
  return templates
    .filter((t) => isStarterTemplate(t) && !!eventTypeId && t.eventTypeId === eventTypeId)
    .map((t) => t.id);
}

export interface EventTemplatePickerProps {
  templates: TemplateItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  eventTypeId?: string;
  isLoading?: boolean;
}

/**
 * Checkboxes over the office's task list, timeline and kibuddim templates, so
 * a new event can start with them already copied in rather than empty.
 */
export function EventTemplatePicker({
  templates,
  selected,
  onChange,
  eventTypeId,
  isLoading,
}: EventTemplatePickerProps) {
  const starters = templates.filter(isStarterTemplate);

  if (isLoading) {
    return <div className="h-16 rounded-xl bg-gray-100 animate-pulse" aria-busy />;
  }
  if (starters.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No templates yet. Save a task list or timeline as a template and it will be offered here.
      </p>
    );
  }

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  return (
    <div className="space-y-3">
      {STARTER_TEMPLATE_TYPES.map((type) => {
        const group = starters.filter((t) => t._type === type);
        if (group.length === 0) return null;
        return (
          <fieldset key={type}>
            <legend className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              {GROUPS[type].icon}
              {GROUPS[type].label}
            </legend>
            <div className="flex flex-wrap gap-2">
              {group.map((t) => {
                const name = t.templateName || t.title || 'Untitled';
                const on = selected.includes(t.id);
                const suggested = !!eventTypeId && t.eventTypeId === eventTypeId;
                return (
                  <label
                    key={t.id}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm cursor-pointer transition-colors ${
                      on
                        ? 'border-primary-500 bg-primary-50 text-primary-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(t.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {name}
                    {suggested && (
                      <span className="text-[10px] font-semibold uppercase text-primary-600">
                        Suggested
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
